import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, notification, 
  Select, Typography, Space, Tag, Tooltip, DatePicker
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  FilterOutlined, EyeOutlined, AuditOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
  SearchOutlined, ReloadOutlined, UserOutlined, DollarOutlined
} from "@ant-design/icons";
import moment from 'moment';

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function ApprovalWorkflow() {
  const history = useHistory();
  const [workflowList, setWorkflowList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    action: '',
    approver_name: '',
    expenditure_id: '',
    start_date: '',
    end_date: ''
  });

  const [showFilter, setShowFilter] = useState(true);

  const columns = [
    {
      title: '#',
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 60,
      align: 'center',
    },
    {
      title: 'Transaction ID',
      dataIndex: "txn_id",
      key: "txn_id",
      fixed: 'left',
      width: 150,
      render: (text, record) => (
        <div>
          <Text strong style={{ color: '#7a0000' }}>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ID: {record.expenditure_id}
          </Text>
        </div>
      )
    },
    {
      title: 'Beneficiary',
      dataIndex: 'beneficiary_name',
      key: 'beneficiary_name',
      width: 180,
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined style={{ color: '#7a0000' }} />
          <Text>{name}</Text>
        </div>
      )
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose_name',
      key: 'purpose_name',
      width: 180,
      render: (name) => <Text>{name}</Text>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ₹{Number(amount).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action) => {
        const colors = {
          'Submitted': 'blue',
          'Approved': 'success',
          'Rejected': 'error',
          'Cancelled': 'default'
        };
        const icons = {
          'Submitted': <CalendarOutlined />,
          'Approved': <CheckCircleOutlined />,
          'Rejected': <CloseCircleOutlined />,
          'Cancelled': <CloseCircleOutlined />
        };
        return (
          <Tag color={colors[action]} icon={icons[action]}>
            {action}
          </Tag>
        );
      }
    },
    {
      title: 'Approver',
      key: 'approver_full_name',
      width: 150,
      render: (_, record) => (
        <Text>{record.approver_full_name || record.approver_name}</Text>
      )
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      width: 200,
      render: (comments) => (
        <Text style={{ fontSize: '12px' }}>
          {comments ? (comments.length > 50 ? `${comments.substring(0, 50)}...` : comments) : '-'}
        </Text>
      )
    },
    {
      title: 'Action Date',
      dataIndex: 'action_date',
      key: 'action_date',
      width: 150,
      sorter: true,
      render: (date) => (
        <div>
          <div>{moment(date).format('DD MMM YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {moment(date).format('HH:mm')}
          </Text>
        </div>
      )
    },
    {
      title: 'Transaction Date',
      dataIndex: 'txn_date',
      key: 'txn_date',
      width: 120,
      render: (date) => moment(date).format('DD MMM YYYY')
    },
    {
      title: 'Actions',
      key: "actions",
      dataIndex: "actions",
      fixed: 'right',
      width: 100,
      align: 'center',
    }
  ];

  useEffect(() => {
    getWorkflowList();
  }, []);

  const getWorkflowList = async (page = 1, limit = 20, sortField = 'id', sortOrder = 'desc', search = '', filters = {}) => {
    try {
      setLoading(true);
      const sortParam = `&sortField=${sortField}&sortOrder=${sortOrder}`;
      const searchParam = search ? `&search=${search}` : '';
      const filterParams = Object.keys(filters)
        .filter(key => filters[key])
        .map(key => `${key}=${filters[key]}`)
        .join('&');
      
      const response = await axios.get(
        `${baseUrl}/approval-workflow/list?page=${page}&limit=${limit}${sortParam}${searchParam}&${filterParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          }
        }
      );
      
      if (response.data.status) {
        setWorkflowList(response.data.result);
        setPagination({
          current: page,
          pageSize: limit,
          total: response.data.totalCount
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch approval workflow data',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    
    setSortField(field);
    setSortOrder(formattedOrder);
    setCurrentPage(current);
    setLimit(pageSize);
    
    getWorkflowList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      handleFilterChange('start_date', dates[0].format('YYYY-MM-DD'));
      handleFilterChange('end_date', dates[1].format('YYYY-MM-DD'));
    } else {
      handleFilterChange('start_date', '');
      handleFilterChange('end_date', '');
    }
  };

  const handleSearch = () => {
    getWorkflowList(1, limit, sortField, sortOrder, search, filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      action: '',
      approver_name: '',
      expenditure_id: '',
      start_date: '',
      end_date: ''
    };
    setFilters(resetFilters);
    setSearch('');
    getWorkflowList(1, limit, sortField, sortOrder, '', resetFilters);
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Approval Workflow History</span>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }}
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
                title={() => (
                  <div style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]} align="middle">
                      <Col>
                        <Button
                          icon={<FilterOutlined />}
                          onClick={() => setShowFilter(!showFilter)}
                          style={{ borderRadius: '6px' }}
                        >
                          {showFilter ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                      </Col>
                      <Col>
                        <Input
                          placeholder="Search by transaction ID, beneficiary, approver..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: 350, borderRadius: '6px' }}
                          suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                        />
                      </Col>
                    </Row>

                    {showFilter && (
                      <div style={{
                        marginTop: '16px',
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <Row gutter={[12, 12]}>
                          <Col span={6}>
                            <Select
                              placeholder="Action Type"
                              value={filters.action}
                              onChange={value => handleFilterChange('action', value)}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              <Option value="">All Actions</Option>
                              <Option value="Submitted">Submitted</Option>
                              <Option value="Approved">Approved</Option>
                              <Option value="Rejected">Rejected</Option>
                              <Option value="Cancelled">Cancelled</Option>
                            </Select>
                          </Col>
                          <Col span={6}>
                            <Input
                              placeholder="Approver Name"
                              value={filters.approver_name}
                              onChange={e => handleFilterChange('approver_name', e.target.value)}
                            />
                          </Col>
                          <Col span={6}>
                            <Input
                              placeholder="Expenditure ID"
                              value={filters.expenditure_id}
                              onChange={e => handleFilterChange('expenditure_id', e.target.value)}
                              type="number"
                            />
                          </Col>
                          <Col span={6}>
                            <RangePicker
                              style={{ width: '100%' }}
                              onChange={handleDateRangeChange}
                              format="DD-MM-YYYY"
                              placeholder={['Start Date', 'End Date']}
                            />
                          </Col>
                          <Col span={24}>
                            <Space>
                              <Button
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: '6px' }}
                              >
                                Search
                              </Button>
                              <Button
                                icon={<ReloadOutlined />}
                                onClick={handleResetFilters}
                                style={{ borderRadius: '6px' }}
                              >
                                Reset Filters
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                )}
                dataSource={workflowList.map((workflow, index) => ({
                  key: (limit * (currentPage - 1)) + index + 1,
                  ...workflow,
                  actions: (
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => history.push(`/expenditures/view/${workflow.expenditure_id}`)}
                    >
                      View
                    </Button>
                  )
                }))}
                pagination={{
                  ...pagination,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} workflow entries`,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                }}
                onChange={handleTableChange}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ApprovalWorkflow;